/**
 * Логика публикации списка «Карточки SEO»: словарь состояний, причины пропуска,
 * сборка полезной нагрузки и подписи результатов. React здесь нет намеренно —
 * всё проверяется тестом без рендера.
 */

import type { BadgeVariant } from '../../components/ui'
import type {
  SeoCenterItem,
  SeoMarketplace,
  SeoModerationStatus,
  SeoPublishBatch,
  SeoPublishBatchStatus,
  SeoPublishCapabilities,
  SeoPublishField,
  SeoPublishPreview,
  SeoPublishTaskStatus,
  SeoRevision,
} from '../seo/api'
import { formatNumber } from './lib'

/** Наружу уходят только наименование и описание: аннотацию площадки не берут. */
export const PUBLISH_FIELDS: readonly SeoPublishField[] = ['title', 'description']

/**
 * Период автоопроса активных партий, мс. Без опроса партия выглядит зависшей,
 * и оператор запускает вторую на те же карточки.
 */
export const PUBLISH_POLL_MS = 4000

/** Сколько последних партий показывает журнал публикаций. */
export const PUBLISH_BATCHES_LIMIT = 20

export const publishBatchLabels: Record<SeoPublishBatchStatus, { label: string; variant: BadgeVariant }> = {
  queued: { label: 'В очереди', variant: 'blue' },
  running: { label: 'Отправляется', variant: 'blue' },
  completed: { label: 'Завершена', variant: 'green' },
  completed_with_errors: { label: 'Завершена с ошибками', variant: 'yellow' },
  failed: { label: 'Не выполнена', variant: 'red' },
  cancelled: { label: 'Остановлена', variant: 'gray' },
}

export const publishTaskLabels: Record<SeoPublishTaskStatus, { label: string; variant: BadgeVariant }> = {
  queued: { label: 'Ждёт отправки', variant: 'gray' },
  sending: { label: 'Отправляется', variant: 'blue' },
  awaiting_moderation: { label: 'Ждёт ответа площадки', variant: 'blue' },
  success: { label: 'Принято', variant: 'green' },
  partial: { label: 'Принято с изменениями', variant: 'yellow' },
  failed: { label: 'Ошибка', variant: 'red' },
  skipped: { label: 'Пропущено', variant: 'gray' },
}

export const moderationLabels: Record<SeoModerationStatus, { label: string; variant: BadgeVariant }> = {
  unknown: { label: 'Нет данных', variant: 'gray' },
  pending: { label: 'На проверке площадки', variant: 'blue' },
  accepted: { label: 'Площадка приняла', variant: 'green' },
  altered: { label: 'Площадка изменила текст', variant: 'yellow' },
  rejected: { label: 'Площадка отклонила', variant: 'red' },
}

/**
 * Причины, по которым карточка не попадёт в партию. Это единственное
 * объяснение, почему выбрано сто карточек, а уйдёт шестьдесят: без причины
 * оператор считает, что система потеряла его выборку.
 */
export const publishSkipLabels: Record<string, string> = {
  content_missing: 'Карточка ещё не создана',
  not_approved: 'Текст не согласован',
  no_revision: 'Нет версии текста',
  blocking_issues: 'Есть блокирующие ошибки',
  already_published: 'Эта версия уже опубликована',
  empty_fields: 'Выбранные поля пусты',
}

export function publishSkipLabel(reason: string | null): string {
  if (!reason) return 'Не публикуется'
  return publishSkipLabels[reason] || 'Не публикуется'
}

/**
 * Судьба партии одной строкой. «Всего» и «принято» показываются всегда, даже
 * нулями: «принято 0» — это ответ, а пропущенная строка читается как «не
 * посчитали». Ошибки и пропуски при нуле опускаются, чтобы удачная партия не
 * выглядела отчётом о проблемах.
 */
export function describeBatch(batch: Pick<SeoPublishBatch, 'total' | 'succeeded' | 'failed' | 'skipped'>): string {
  const parts = [`всего ${formatNumber(batch.total)}`, `принято ${formatNumber(batch.succeeded)}`]
  if (batch.failed) parts.push(`с ошибкой ${formatNumber(batch.failed)}`)
  if (batch.skipped) parts.push(`пропущено ${formatNumber(batch.skipped)}`)
  return parts.join(' · ')
}

/** Партия ещё живёт: имеет смысл опрашивать её состояние. */
export function isBatchActive(batch: Pick<SeoPublishBatch, 'status'>): boolean {
  return batch.status === 'queued' || batch.status === 'running'
}

/** Плановая партия ждёт своей даты — это не «зависла», а «ещё не время». */
export function scheduleHint(batch: Pick<SeoPublishBatch, 'scheduled_at'>): string | null {
  if (!batch.scheduled_at) return null
  const date = new Date(batch.scheduled_at)
  if (Number.isNaN(date.getTime())) return null
  if (date.getTime() <= Date.now()) return null
  return `Запуск ${date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })}`
}

/**
 * Локальное время поля «Запустить не раньше» переводится в ISO: сервер живёт
 * в UTC, и без явного перевода вечерний запуск уехал бы на другой час.
 */
export function toScheduleIso(localValue: string): string | null {
  if (!localValue) return null
  const date = new Date(localValue)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

/** Момент времени по-русски; пусто и мусор — прочерк. */
export function formatMoment(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
}

/**
 * Область партии. Ручная выборка и кампания взаимоисключающие: у кампании своя
 * область и свой срок, и смешение дало бы партию, которую нельзя объяснить.
 */
export type PublishScope =
  | { kind: 'articles'; articles: string[] }
  | { kind: 'campaign'; campaignId: number }

/** Кампания выбрана — область её; иначе область — отмеченные карточки. */
export function publishScope(articles: string[], campaignId: number | null): PublishScope {
  if (campaignId) return { kind: 'campaign', campaignId }
  return { kind: 'articles', articles }
}

/** Область непуста: есть что отправлять. */
export function scopeIsRunnable(scope: PublishScope): boolean {
  return scope.kind === 'campaign' ? scope.campaignId > 0 : scope.articles.length > 0
}

export interface PublishPayload {
  marketplace: SeoMarketplace
  seller_id: number
  articles?: string[]
  campaign_id?: number
  fields: SeoPublishField[]
  scheduled_at?: string | null
  comment?: string | null
}

/**
 * Полезная нагрузка предпросмотра и создания партии.
 *
 * `force` не передаётся никогда и ни при каких условиях: он обходит проверки
 * публикации, то есть отправляет наружу то, что проверки не пропустили.
 */
export function publishPayload(params: {
  marketplace: SeoMarketplace
  sellerId: number
  scope: PublishScope
  scheduledAt?: string | null
  comment?: string | null
}): PublishPayload {
  const payload: PublishPayload = {
    marketplace: params.marketplace,
    seller_id: params.sellerId,
    fields: [...PUBLISH_FIELDS],
  }
  if (params.scope.kind === 'campaign') payload.campaign_id = params.scope.campaignId
  else payload.articles = params.scope.articles
  if (params.scheduledAt !== undefined) payload.scheduled_at = params.scheduledAt
  if (params.comment !== undefined) payload.comment = params.comment
  return payload
}

/**
 * Можно ли предлагать публикацию по этой выборке. Согласованные карточки — это
 * состояние `ready`. Артикулы, которых нет среди загруженных строк (выбраны на
 * другой странице или «всё по фильтру»), считаются возможными: доказать их
 * непригодность нечем, а окно предпросмотра всё равно назовёт причину по
 * каждой карточке.
 */
export function selectionHasPublishable(
  selected: string[],
  items: Array<Pick<SeoCenterItem, 'article' | 'state'>>,
): boolean {
  if (!selected.length) return false
  const byArticle = new Map(items.map((item) => [item.article, item.state]))
  return selected.some((article) => {
    const state = byArticle.get(article)
    return state === undefined || state === 'ready'
  })
}

/** Строка «Что уйдёт: N из M» над списком предпросмотра. */
export function previewSummaryLine(preview: Pick<SeoPublishPreview, 'publishable' | 'total'>): string {
  return `Что уйдёт: ${formatNumber(preview.publishable)} из ${formatNumber(preview.total)}`
}

/** Обрезка описания в предпросмотре: карточка не должна занимать экран. */
export function truncateText(value: string | undefined | null, limit = 220): string {
  const text = (value || '').trim()
  if (text.length <= limit) return text
  return `${text.slice(0, limit).trimEnd()}…`
}

export interface PublishModeBannerContent {
  dryRun: boolean
  title: string
  description: string
  note: string | null
}

/**
 * Содержимое плашки режима. Плашка стоит ДО кнопки: оператор обязан понимать,
 * чем кончится нажатие, до того как нажмёт, а не узнавать это из результата.
 * Неизвестные возможности считаются сухим прогоном — предполагать отправку
 * наружу при неизвестном состоянии нельзя.
 */
export function publishModeBanner(capabilities: SeoPublishCapabilities | null): PublishModeBannerContent {
  const dryRun = capabilities?.dry_run !== false
  if (dryRun) {
    return {
      dryRun: true,
      title: 'Режим предпросмотра: наружу ничего не отправляется',
      description:
        capabilities?.dry_run_reason
        || (capabilities
          ? 'Публикация ещё не включена владельцем.'
          : 'Состояние публикации неизвестно — до ответа сервера отправка считается выключенной.'),
      note: capabilities?.note || null,
    }
  }
  const allowed = capabilities?.allowed_seller_ids || []
  return {
    dryRun: false,
    title: 'Публикация включена — отправка уходит на площадку',
    description: allowed.length
      ? `Кабинеты с разрешённой отправкой: ${allowed.join(', ')}`
      : 'Список разрешённых кабинетов не задан: отправка возможна из любого.',
    note: capabilities?.note || null,
  }
}

/** Тост по итогу создания партии; в сухом прогоне прямо говорит об этом. */
export function createBatchToast(result: {
  batch: Pick<SeoPublishBatch, 'total'>
  dry_run: boolean
}): { text: string; tone: 'success' | 'info' } {
  const base = `Партия на ${formatNumber(result.batch.total)} карточек создана`
  return result.dry_run
    ? { text: `${base}. Режим предпросмотра: наружу ничего не ушло`, tone: 'info' }
    : { text: base, tone: 'success' }
}

/** Тост повтора ошибочных задач; ноль — это ответ, а не молчание. */
export function retryToast(retried: number): { text: string; tone: 'success' | 'info' } {
  return retried
    ? { text: `Повторно отправляется карточек: ${formatNumber(retried)}`, tone: 'success' }
    : { text: 'Повторять нечего: ошибочных задач в партии не осталось', tone: 'info' }
}

/** Тост опроса площадки; «ответов нет» отличается от ошибки запроса. */
export function moderationSyncToast(closed: number): { text: string; tone: 'success' | 'info' } {
  return closed
    ? { text: `Получен ответ площадки по ${formatNumber(closed)} карточкам`, tone: 'success' }
    : { text: 'Новых ответов площадки нет', tone: 'info' }
}

/**
 * Тост разрешения расхождения. Последствия у решений разные, поэтому пишется
 * не «готово», а что именно произошло с текстом.
 */
export function resolveDiscrepancyToast(
  decision: 'keep_local' | 'accept_remote',
  dryRun: boolean,
): { text: string; tone: 'success' | 'info' } {
  if (decision === 'accept_remote') {
    return { text: 'Текст площадки принят как черновик — проверьте и согласуйте его', tone: 'success' }
  }
  return dryRun
    ? {
      text: 'Наша версия поставлена в очередь публикации. Режим предпросмотра: наружу ничего не ушло',
      tone: 'info',
    }
    : { text: 'Наша версия поставлена в очередь публикации', tone: 'success' }
}

/**
 * Наша версия текста: черновик, а при его отсутствии — опубликованная.
 * Список редакций приходит новыми вперёд, но полагаться на порядок нельзя,
 * поэтому берётся наибольший номер редакции.
 */
export function pickLocalRevision(revisions: SeoRevision[]): SeoRevision | null {
  const own = revisions.filter((revision) => revision.kind === 'draft')
  const source = own.length ? own : revisions.filter((revision) => revision.kind === 'published')
  return source.reduce<SeoRevision | null>(
    (best, revision) => (!best || revision.revision > best.revision ? revision : best),
    null,
  )
}

/** Версия площадки — последний снимок, снятый сверкой. */
export function pickRemoteRevision(revisions: SeoRevision[]): SeoRevision | null {
  return revisions
    .filter((revision) => revision.kind === 'remote_snapshot')
    .reduce<SeoRevision | null>(
      (best, revision) => (!best || revision.revision > best.revision ? revision : best),
      null,
    )
}

/** Расхождение вообще есть: наша версия и версия площадки разошлись. */
export function isDiscrepancySync(syncState: string | null | undefined): boolean {
  return syncState === 'conflict' || syncState === 'remote_ahead'
}

/** Подпись расхождения: обе стороны правили или площадка ушла вперёд. */
export function discrepancyLabel(syncState: string | null | undefined): string {
  return syncState === 'conflict' ? 'Изменено с обеих сторон' : 'Площадка впереди'
}
